import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type MarketSettingsPropertyTagsRoute from 'lumen/routes/market/settings/property-tags';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class MarketSettingsPropertyTagsController extends Controller {
  declare model: ModelFrom<MarketSettingsPropertyTagsRoute>;

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  saveChangesTask = task(async (): Promise<void> => {
    const requestUrl = formatUrl(`/property-tags`, {
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
      market_id: this.hotelAndMarketService.market.id,
    });

    const requestBody = {
      property_tags: this.dirtyPropertyTags.map((tag) =>
        tag.serialize({ includeId: true }),
      ),
    };

    try {
      const propertyTagsResponse = await this.ajax.request<object>(requestUrl, {
        namespace: APIGATEWAY,
        type: 'PATCH',
        data: JSON.stringify(requestBody),
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('property-tags', propertyTagsResponse);
      this.dirtyPropertyTags.forEach((item) => item.updateRelationships());

      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } catch (error: unknown) {
      this.toast.error(this.intl.t('general.changes_saved.failed'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });

      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dirtyPropertyTags() {
    if (!this.propertyTags) {
      return [];
    }

    return this.propertyTags.filter((tag) => tag.hasUnsavedChanges);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasUnsavedChanges() {
    return this.dirtyPropertyTags.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get propertyTags() {
    return this.model.propertyTags.last?.value ?? null;
  }

  @action
  async cancelChanges(): Promise<void> {
    const isCancelConfirmed = await this.showDiscardChangesAlert();

    if (!isCancelConfirmed) {
      return;
    }

    this.discardChanges();
  }

  @action discardChanges(): void {
    this.dirtyPropertyTags.forEach((tag) => {
      tag.rollbackAttributes();
      tag.rollbackRelationships();
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  showDiscardChangesAlert() {
    return this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.property_tags.discard_changes_modal.title'),
      text: this.intl.t(
        'settings.property_tags.discard_changes_modal.description',
      ),
      buttons: {
        cancel: {
          text: this.intl.t(
            'settings.property_tags.discard_changes_modal.cta.cancel',
          ),
        },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }
}
